<template>
  <div class="minimal-test-set-card h-full flex flex-col">
    <div class="test-set-container flex-1 overflow-y-auto">
      <!-- 加载状态 -->
      <div v-if="isLoading" class="loading-state h-full flex flex-col">
        <div class="loading-message mb-4 flex-shrink-0 text-center">
          <div class="inline-flex items-center rounded-lg bg-blue-50 px-4 py-2">
            <icon-vue icon="mdi:loading" class="mr-2 animate-spin text-blue-600" />
            <span class="text-sm font-medium text-blue-700">{{ loadingMessage }}</span>
          </div>
        </div>

        <!-- 骨架屏动画 -->
        <div class="flex-1 overflow-hidden space-y-3">
          <div v-for="i in 3" :key="i" class="animate-pulse">
            <div class="flex items-start border border-gray-200 rounded-lg p-3">
              <div class="mr-3 mt-0.5 h-4 w-4 rounded bg-gray-300"></div>
              <div class="flex-1">
                <div class="mb-2 h-4 w-3/4 rounded bg-gray-300"></div>
                <div class="mb-1 h-3 w-1/2 rounded bg-gray-200"></div>
                <div class="h-2 w-1/3 rounded bg-gray-200"></div>
              </div>
              <div class="h-6 w-12 rounded bg-gray-300"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- 错误状态 -->
      <div v-else-if="isError" class="error-state py-8 text-center">
        <div class="relative mb-4">
          <div
            class="mx-auto h-16 w-16 flex items-center justify-center border-2 border-gray-200 rounded-full bg-gray-100"
          >
            <icon-vue icon="mdi:shield-check" class="h-8 w-8 text-gray-400" />
          </div>
        </div>
        <h3 class="mb-2 text-base font-medium text-gray-700">测试用例推荐未完成</h3>
        <p class="text-sm leading-relaxed text-gray-500">
          需要重新分析才能生成测试用例推荐
          <br />
          <span class="text-xs text-gray-400">点击顶部"重新分析"按钮开始</span>
        </p>
      </div>

      <!-- 测试用例列表 -->
      <div v-else-if="hasTestCases" class="test-cases-list h-full flex flex-col">
        <!-- 顶部操作栏 -->
        <div class="mb-4 flex-shrink-0">
          <!-- 选择状态栏 - iOS风格紧凑布局 -->
          <div
            class="selection-bar mb-3 flex items-center justify-between border border-blue-100 rounded-lg from-blue-50 to-indigo-50 bg-gradient-to-r p-3"
          >
            <div class="flex flex-1 items-center gap-3 min-w-0">
              <a-checkbox
                v-model:checked="selectAll"
                :indeterminate="indeterminate"
                class="flex-shrink-0 text-blue-600"
                @change="handleSelectAll"
              >
                <span class="text-sm font-medium text-gray-700">全选</span>
              </a-checkbox>

              <!-- 选择计数徽章 -->
              <div class="flex flex-shrink-0 items-center gap-2">
                <div class="status-badge status-badge-selected">
                  <span class="badge-count">{{ selectedCases.length }}</span>
                  <span class="badge-separator">/</span>
                  <span class="badge-total">{{ testCases.length }}</span>
                </div>
              </div>
            </div>

            <!-- 统计信息徽章 -->
            <div class="flex flex-shrink-0 items-center gap-2">
              <div class="status-badge status-badge-adopted">
                <icon-vue icon="mdi:check-circle" class="badge-icon" />
                <span class="badge-count">{{ adoptedCases.length }}</span>
              </div>
              <div class="status-badge status-badge-pending">
                <icon-vue icon="mdi:clock-outline" class="badge-icon" />
                <span class="badge-count">{{ testCases.length - adoptedCases.length }}</span>
              </div>
            </div>
          </div>

          <!-- 操作按钮栏 -->
          <div class="flex items-center space-x-2" style="margin-left: 10px">
            <a-button
              type="primary"
              size="small"
              :disabled="selectedCases.length === 0"
              :loading="batchAdopting"
              class="h-7 px-2 py-1 text-xs"
              @click="handleBatchAdopt"
            >
              <template #icon><plus-outlined class="text-xs" /></template>
              <span style="font-size: 12px">采纳选中</span>
              <span v-if="selectedCases.length > 0" class="ml-1 text-xs">({{ selectedCases.length }})</span>
            </a-button>

            <a-button
              size="small"
              :disabled="testCases.length === 0 || allAdopted"
              :loading="allAdopting"
              class="h-7 border-gray-300 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50"
              @click="handleAdoptAll"
            >
              <template #icon><check-outlined class="text-xs" /></template>
              <span style="font-size: 12px">{{ allAdopted ? '全部已采纳' : '全部采纳' }}</span>
            </a-button>

            <!-- 快捷操作 -->
            <div class="flex-1"></div>
            <a-dropdown v-if="testCases.length > 0">
              <a-button
                size="small"
                class="h-7 border-none px-1.5 py-1 text-xs text-gray-400 shadow-none hover:text-gray-600"
              >
                <template #icon><icon-vue icon="mdi:dots-horizontal" class="text-sm" /></template>
              </a-button>
              <template #overlay>
                <a-menu class="text-xs">
                  <a-menu-item @click="handleSelectByPriority('critical')">
                    <icon-vue icon="mdi:star" class="mr-1 text-xs text-red-500" />
                    <span class="text-xs">选择关键用例</span>
                  </a-menu-item>
                  <a-menu-item @click="handleSelectByPriority('high')">
                    <icon-vue icon="mdi:arrow-up" class="mr-1 text-xs text-orange-500" />
                    <span class="text-xs">选择高优先级</span>
                  </a-menu-item>
                  <a-menu-item @click="handleClearSelection">
                    <icon-vue icon="mdi:close" class="mr-1 text-xs text-gray-500" />
                    <span class="text-xs">清空选择</span>
                  </a-menu-item>
                </a-menu>
              </template>
            </a-dropdown>
          </div>
        </div>

        <!-- 用例列表 -->
        <div class="flex-1 overflow-y-auto space-y-3">
          <test-case-item
            v-for="(testCase, index) in testCases"
            :key="testCase.test_case_id"
            :test-case="testCase"
            :index="index"
            :selected="selectedCases.includes(testCase.test_case_id)"
            :adopted="adoptedCases.includes(testCase.test_case_id)"
            @select="handleSelectCase"
            @adopt="handleAdoptSingle"
            @view-detail="handleViewDetail"
          />
        </div>
      </div>

      <!-- 空状态 - 等待分析 -->
      <div v-else-if="status === 'idle'" class="empty-state py-8 text-center">
        <div class="relative mb-4">
          <div
            class="relative mx-auto h-12 w-12 flex items-center justify-center rounded-full from-blue-500 to-cyan-600 bg-gradient-to-r shadow-lg"
          >
            <icon-vue icon="mdi:shield-check" class="animate-pulse text-xl text-white" />
            <!-- 波纹效果 -->
            <div
              class="absolute inset-0 animate-ping border-2 border-blue-300 rounded-full opacity-60"
              style="animation-duration: 3s"
            ></div>
            <div
              class="absolute inset-0 animate-ping border-2 border-cyan-300 rounded-full opacity-40"
              style="animation-delay: 1s; animation-duration: 3s"
            ></div>
          </div>
        </div>
        <h3 class="mb-2 text-sm font-medium text-gray-700">智能最小测试集</h3>
        <p class="text-xs leading-relaxed text-gray-500">
          AI将分析代码变更，智能推荐
          <br />
          <span class="font-medium text-blue-600">最优测试用例组合</span>
          ，提升测试效率
        </p>
        <div class="mt-4 flex justify-center">
          <div class="flex items-center text-xs text-gray-400 space-x-2">
            <span class="h-2 w-2 animate-pulse rounded-full bg-blue-500"></span>
            <span>等待分析启动</span>
          </div>
        </div>
      </div>

      <!-- 空状态 - 分析完成但无推荐 -->
      <div v-else class="no-data-state py-8 text-center">
        <div class="relative mb-4">
          <div
            class="mx-auto h-16 w-16 flex items-center justify-center border-2 border-gray-200 rounded-full bg-gray-50"
          >
            <icon-vue icon="mdi:shield-check" class="h-8 w-8 text-gray-400" />
          </div>
        </div>
        <h3 class="mb-2 text-base font-medium text-gray-700">暂无推荐测试用例</h3>
        <p class="text-sm leading-relaxed text-gray-500">
          本次代码变更未匹配到相关测试用例
          <br />
          <span class="text-xs text-gray-400">可能是新功能或测试用例库待完善</span>
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { AnalysisStatus, TestCaseCorrelation } from '@/types/precision-new/ai-analysis';
import { CheckOutlined, PlusOutlined } from '@ant-design/icons-vue';
import { Icon as IconVue } from '@iconify/vue';
import { message } from 'ant-design-vue';
import { computed, ref, watch } from 'vue';
import TestCaseItem from './TestCaseItem.vue';

// Props
interface Props {
  status: AnalysisStatus;
  testCases: TestCaseCorrelation[];
  loading?: boolean;
  loadingMessage?: string;
  error?: string;
  selectedCases?: string[];
  adoptedCases?: string[];
}

const props = withDefaults(defineProps<Props>(), {
  testCases: () => [],
  loading: false,
  loadingMessage: '正在分析测试关联...',
  error: '',
  selectedCases: () => [],
  adoptedCases: () => [],
});

// Emits
const emit = defineEmits(['retry', 'viewDetail', 'selectCase', 'adoptCase', 'batchAdopt', 'adoptAll']);

// 状态
const selectedCases = ref<string[]>([...props.selectedCases]);
const selectAll = ref(false);
const indeterminate = ref(false);
const batchAdopting = ref(false);
const allAdopting = ref(false);

// Computed
const isLoading = computed(() => props.loading || props.status === 'analyzing');
const isError = computed(() => props.status === 'error' || !!props.error);
const hasTestCases = computed(() => props.testCases && props.testCases.length > 0);

const allAdopted = computed(() => {
  if (props.testCases.length === 0) {
    return false;
  }
  return props.testCases.every((testCase) => props.adoptedCases.includes(testCase.test_case_id));
});

// 监听props变化，同步selectedCases
watch(
  () => props.selectedCases,
  (newVal) => {
    selectedCases.value = [...newVal];
    updateSelectAllState();
  },
  { immediate: true },
);

// 方法
function handleViewDetail(testCase: TestCaseCorrelation) {
  emit('viewDetail', testCase);
}

function handleSelectCase(caseId: string, checked: boolean) {
  if (checked) {
    selectedCases.value.push(caseId);
  } else {
    selectedCases.value = selectedCases.value.filter((id) => id !== caseId);
  }
  updateSelectAllState();
  emit('selectCase', caseId, checked);
}

function handleSelectAll(e: any) {
  const checked = e.target.checked;
  if (checked) {
    // 只选择未被采纳的用例
    const availableCases = props.testCases
      .filter((testCase) => !props.adoptedCases.includes(testCase.test_case_id))
      .map((testCase) => testCase.test_case_id);

    selectedCases.value = availableCases;
    // 批量触发选择事件
    availableCases.forEach((caseId) => {
      emit('selectCase', caseId, true);
    });
  } else {
    // 清空所有选择
    const previousSelected = [...selectedCases.value];
    selectedCases.value = [];
    // 批量触发取消选择事件
    previousSelected.forEach((caseId) => {
      emit('selectCase', caseId, false);
    });
  }
  updateSelectAllState();
}

function updateSelectAllState() {
  const availableCases = props.testCases.filter((testCase) => !props.adoptedCases.includes(testCase.test_case_id));
  const selectedCount = selectedCases.value.length;
  const availableCount = availableCases.length;

  selectAll.value = selectedCount > 0 && selectedCount === availableCount;
  indeterminate.value = selectedCount > 0 && selectedCount < availableCount;
}

async function handleAdoptSingle(caseId: string) {
  try {
    emit('adoptCase', caseId);
    // 从选中列表中移除已采纳的用例
    selectedCases.value = selectedCases.value.filter((id) => id !== caseId);
    updateSelectAllState();
  } catch (error) {
    console.error('采纳测试用例失败:', error);
  }
}

async function handleBatchAdopt() {
  if (selectedCases.value.length === 0) {
    message.warning('请先选择要采纳的测试用例');
    return;
  }

  batchAdopting.value = true;
  try {
    emit('batchAdopt', [...selectedCases.value]);
    selectedCases.value = [];
    updateSelectAllState();
  } catch (error) {
    console.error('批量采纳失败:', error);
  } finally {
    batchAdopting.value = false;
  }
}

async function handleAdoptAll() {
  const unadoptedCases = props.testCases
    .filter((testCase) => !props.adoptedCases.includes(testCase.test_case_id))
    .map((testCase) => testCase.test_case_id);

  if (unadoptedCases.length === 0) {
    message.info('所有测试用例已采纳');
    return;
  }

  allAdopting.value = true;
  try {
    emit('adoptAll', unadoptedCases);
    selectedCases.value = [];
    updateSelectAllState();
  } catch (error) {
    console.error('全部采纳失败:', error);
  } finally {
    allAdopting.value = false;
  }
}

// 快捷选择方法
function handleSelectByPriority(priority: string) {
  const priorityCases = props.testCases
    .filter(
      (testCase) => testCase.test_case_priority === priority && !props.adoptedCases.includes(testCase.test_case_id),
    )
    .map((testCase) => testCase.test_case_id);

  // 添加到现有选择中
  priorityCases.forEach((caseId) => {
    if (!selectedCases.value.includes(caseId)) {
      selectedCases.value.push(caseId);
      emit('selectCase', caseId, true);
    }
  });

  updateSelectAllState();

  if (priorityCases.length > 0) {
    message.success(`已选择 ${priorityCases.length} 个${priority === 'critical' ? '关键' : '高优先级'}用例`);
  } else {
    message.info(`没有找到${priority === 'critical' ? '关键' : '高优先级'}用例`);
  }
}

function handleClearSelection() {
  const previousCount = selectedCases.value.length;
  selectedCases.value.forEach((caseId) => {
    emit('selectCase', caseId, false);
  });
  selectedCases.value = [];
  updateSelectAllState();

  if (previousCount > 0) {
    message.success(`已清空 ${previousCount} 个用例的选择`);
  }
}
</script>

<style scoped>
.minimal-test-set-card {
  height: 100%;
}

.test-set-container {
  position: relative;
}

.test-set-container::-webkit-scrollbar {
  width: 6px;
}

.test-set-container::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.test-set-container::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.test-set-container::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* 动画效果 */
@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

@keyframes ping {
  75%,
  100% {
    transform: scale(2);
    opacity: 0;
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-pulse {
  animation: pulse 2s infinite;
}

.animate-ping {
  animation: ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;
}

.animate-spin {
  animation: spin 1s linear infinite;
}

/* iOS风格状态徽章 */
.selection-bar {
  transition: all 0.3s ease;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  transition: all 0.2s ease;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', sans-serif;
}

/* 选中计数徽章 - 蓝色 */
.status-badge-selected {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.12), rgba(99, 102, 241, 0.12));
  border: 1px solid rgba(59, 130, 246, 0.2);
  color: #2563eb;
}

.status-badge-selected .badge-count {
  color: #1e40af;
  font-weight: 700;
}

.status-badge-selected .badge-separator {
  color: #94a3b8;
  font-weight: 500;
  margin: 0 2px;
}

.status-badge-selected .badge-total {
  color: #64748b;
  font-weight: 500;
}

/* 已采纳徽章 - 绿色 */
.status-badge-adopted {
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.12), rgba(22, 163, 74, 0.12));
  border: 1px solid rgba(34, 197, 94, 0.2);
  color: #15803d;
}

.status-badge-adopted .badge-icon {
  font-size: 14px;
  color: #16a34a;
}

/* 待采纳徽章 - 灰色 */
.status-badge-pending {
  background: linear-gradient(135deg, rgba(148, 163, 184, 0.12), rgba(100, 116, 139, 0.12));
  border: 1px solid rgba(148, 163, 184, 0.2);
  color: #475569;
}

.status-badge-pending .badge-icon {
  font-size: 14px;
  color: #64748b;
}

/* hover效果 */
.status-badge:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

/* 响应式 - 小屏幕优化 */
@media (max-width: 480px) {
  .selection-bar {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }

  .status-badge {
    padding: 3px 8px;
    font-size: 11px;
  }

  .status-badge .badge-icon {
    font-size: 12px;
  }
}
</style>
